import { HStack, Tag, Text } from '@chakra-ui/react';
import { useDocumentVersionListQuery } from '@client/graphql/__generated__/document-operations';
import {
  DocumentVersionListFragment,
  DocumentVersionStatus,
} from '@client/graphql/__generated__/types';
import { FC, memo, useCallback, useMemo } from 'react';
import { Select, SelectProps } from './Select';

export interface DocumentVersionSelectOption {
  label: string;
  value: string;
  documentVersion?: DocumentVersionListFragment;
}
interface DocumentVersionSelectProps
  extends SelectProps<DocumentVersionSelectOption> {
  documentId?: string;
}

const getStatusColor = (status: DocumentVersionStatus) => {
  switch (status) {
    case DocumentVersionStatus.DRAFT:
      return 'gray';
    case DocumentVersionStatus.ACTIVE:
      return 'green';
    case DocumentVersionStatus.INACTIVE:
      return 'red';
    default:
      return 'gray';
  }
};

export const DocumentVersionSelect: FC<DocumentVersionSelectProps> = memo(
  function DocumentVersionSelect({ documentId, ...props }) {
    const { data } = useDocumentVersionListQuery({
      variables: {
        filters: {
          documentId,
        },
      },
    });

    const formatOptionLabel = useCallback(
      (option: DocumentVersionSelectOption) => {
        const color = getStatusColor(
          option.documentVersion?.status || DocumentVersionStatus.DRAFT
        );

        return (
          <HStack justifyContent="space-between">
            <Text>{option.documentVersion?.version}</Text>
            <Tag colorScheme={color}>{option.documentVersion?.status}</Tag>
          </HStack>
        );
      },
      []
    );

    const options = useMemo(() => {
      return data?.documentVersionList?.results?.map((documentVersion) => ({
        label:
          `${documentVersion.version} (${
            documentVersion.searchRegex || 'Original'
          })` || '',
        value: documentVersion.id || '',
        documentVersion,
      }));
    }, [data?.documentVersionList?.results]);

    return (
      <Select<DocumentVersionSelectOption>
        formatOptionLabel={formatOptionLabel}
        options={options}
        {...props}
      />
    );
  }
);
